import { PaymentMethod } from '@stripe/stripe-js';
import User from 'src/models/User';
import { reactivateSubscription } from 'src/services/reactivation.service';
import { ReactivationScenario } from 'src/services/reactivation.types';
import { startSubscription } from 'src/pages/payment-new/utils/startSubscription.util';
import {
  trackReactivationCompleted,
  trackReactivationFailed,
} from 'src/util/track';
import history from 'src/lib/history';
import { Plan } from 'src/pages/deprecated/payment/types';
import { checkAuthentication } from 'src/lib/auth/actions';

export interface ProcessDirectReactivationParams {
  user: User;
  pmType: string;
  selectedPlanId: string;
  totalPrice: string;
  scenario: ReactivationScenario | null;
  priceId: string;
  dispatch?: any;
  campaignCode?: string | null;
}

export const processDirectReactivation = async ({
  user,
  pmType,
  selectedPlanId,
  totalPrice,
  scenario,
  priceId,
  dispatch,
  campaignCode,
}: ProcessDirectReactivationParams): Promise<void> => {
  await reactivateSubscription({
    userId: user.id,
    email: user.email,
    campaignCode: campaignCode || undefined,
  });

  if (dispatch) {
    try {
      await dispatch(checkAuthentication());
    } catch (e) {
      console.warn(
        'Failed to refresh auth state after direct reactivation:',
        e
      );
    }
  }

  trackReactivationCompleted(
    'page',
    pmType,
    selectedPlanId,
    totalPrice,
    scenario || undefined
  );

  history.push('/success', {
    priceId,
    totalPrice,
  });
};

export interface ProcessNewSubscriptionReactivationParams {
  user: User;
  paymentMethod: PaymentMethod;
  priceId: string;
  productId: string;
  totalPrice: string;
  walletPaymentIntentId: string;
  noIntentAvailable: boolean;
  selectedPlanId: string;
  allPlansSorted: any[];
  effectivePlanObj?: any;
  pmType: string;
  scenario: ReactivationScenario | null;
  dispatch?: any;
  campaignCode?: string | null;
}

export const processNewSubscriptionReactivation = async (
  params: ProcessNewSubscriptionReactivationParams
): Promise<string | null> => {
  const {
    user,
    paymentMethod,
    priceId,
    productId,
    totalPrice,
    walletPaymentIntentId,
    noIntentAvailable,
    selectedPlanId,
    allPlansSorted,
    effectivePlanObj,
    pmType,
    scenario,
    dispatch,
    campaignCode,
  } = params;

  const result = await startSubscription({
    user,
    paymentMethod,
    priceId,
    productId,
    coupon: undefined,
    isChallengeAccepted: false,
    totalPrice,
    futureCommencement: false,
    tagId: 0,
    challengeObject: null,
    walletPaymentIntentId,
    noIntentAvailable,
    selectedPlanId,
    allPlansSorted,
    effectivePlanObj,
    campaignCode: campaignCode || undefined,
  });

  if (result?.errorMsg) {
    trackReactivationFailed(
      'page',
      pmType,
      result.errorMsg,
      selectedPlanId,
      'startSubscription'
    );
    return result.errorMsg;
  }

  if (dispatch) {
    try {
      await dispatch(checkAuthentication());
    } catch (e) {
      console.warn(
        'Failed to refresh auth state after new subscription reactivation:',
        e
      );
    }
  }

  trackReactivationCompleted(
    'page',
    pmType,
    selectedPlanId,
    totalPrice,
    scenario || undefined
  );

  history.push('/success', {
    priceId,
    totalPrice,
  });

  return null;
};

export const getTargetPlanId = (
  subscription: {
    planId?: string;
    priceId?: string;
    planName?: string;
  } | null,
  allPlansSorted: Plan[]
): string | undefined => {
  if (!subscription || !allPlansSorted || allPlansSorted.length === 0) {
    return undefined;
  }

  // 1. Direct match: planName, planId, or priceId matches a plan's id
  const directMatch = allPlansSorted.find(
    p =>
      p.id === subscription.planName ||
      p.id === subscription.planId ||
      p.id === subscription.priceId
  );
  if (directMatch) return directMatch.id;

  // 2. Match via mealPlanVariant priceId / productId
  const variantMatch = allPlansSorted.find(p =>
    Object.values(p.mealPlanVariants || {}).some(
      (v: { id?: string; productId?: string }) =>
        v.id === subscription.priceId ||
        v.productId === subscription.planId ||
        v.id === subscription.planId
    )
  );
  if (variantMatch) return variantMatch.id;

  // No ID match — return undefined so the caller falls back to the 3-month default.
  return undefined;
};
